import * as api from '../core/api/api';
import HttpMethods from '../enums/http-methods';
import * as conditionalUtils from '../core/amplify/utils/conditional-utilities';
import { isEmpty, set } from '../core/object-utils';
import { fetchXcaliberToken } from '../core/amplify/middlewares/xcaliber.middleware';

const CONSTELLATION_SERVICES_LOCATION = '/constellation';

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

const getPatientTimelineWithFilter = async <T>({
  caseRun,
  limit,
  page,
  timelineInfiniteLoad,
  messageTypeFilter,
  dateFilter,
  endDateFilter
}) => {
  const xcaliberToken = fetchXcaliberToken();
  const status = xcaliberToken?.status?.toUpperCase() ?? '';
  if (status === 'SUCCESS') {
    const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken?.tenantPath}/directory/api/v1/entities/patient_timeline/entries/functions/get_patient_timeline/execute`;

    const bodyObject: CMxAPI.CaseEntriesRequestObject = {
      mrn: caseRun.patient_identifier.number,
      limit
    };

    if (conditionalUtils.capabilityValue(timelineInfiniteLoad)) {
      bodyObject.offset = limit * page - limit;
      if (!isEmpty(messageTypeFilter)) {
        const filterData = {
          and: [
            {
              message_type: messageTypeFilter?.map(item => item?.value) ?? []
            }
          ]
        };
        set(bodyObject, 'filter', filterData);
      }

      if (!!dateFilter && !isEmpty(dateFilter)) {
        const andArray = bodyObject?.filter?.and || []; // Use default empty array if bodyObject.filter.and is undefined
        let maxParam = {};
        if (!!endDateFilter && !isEmpty(endDateFilter)) {
          maxParam = {
            max: endDateFilter
          };
        }
        const filterData = {
          and: [
            ...andArray,
            {
              bedside_datetime: {
                min: dateFilter,
                ...maxParam
              }
            }
          ]
        };
        set(bodyObject, 'filter', filterData);
      }
    }
    try {
      const response = await api.requestJSON<
        CMxAPI.PageableList<CMxAPI.CaseEntriesResponse>
      >(queryURL, {
        ...REQUEST_INIT_OPTIONS,
        headers: { Authorization: `Bearer ${xcaliberToken.token}` },
        method: HttpMethods.POST,
        body: JSON.stringify(bodyObject)
      });

      return response;
    } catch (error) {
      throw error;
    }
  }
};

const getPatientTimelineWithoutFilter = async <T>({
  caseRun,
  limit,
  page,
  timelineInfiniteLoad
}) => {
  const xcaliberToken = fetchXcaliberToken();
  const status = xcaliberToken?.status?.toUpperCase() ?? '';
  if (status === 'SUCCESS') {
    const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken?.tenantPath}/directory/api/v1/entities/patient_timeline/entries/functions/get_patient_timeline/execute`;

    const bodyObject: CMxAPI.CaseEntriesRequestObject = {
      mrn: caseRun.patient_identifier.number,
      limit
    };

    if (conditionalUtils.capabilityValue(timelineInfiniteLoad)) {
      bodyObject.offset = limit * page - limit;
    }

    try {
      const response = await api.requestJSON<
        CMxAPI.PageableList<CMxAPI.CaseEntriesResponse>
      >(queryURL, {
        ...REQUEST_INIT_OPTIONS,
        headers: { Authorization: `Bearer ${xcaliberToken.token}` },
        method: HttpMethods.POST,
        body: JSON.stringify(bodyObject)
      });

      return response;
    } catch (error) {
      throw error;
    }
  }
};

const getPatientMessageCountWithFilter = async <T>(timelineCountParams) => {
  const { caseRun, dateFilter, endDateFilter } = timelineCountParams;
  const xcaliberToken = fetchXcaliberToken();

  const status = xcaliberToken?.status?.toUpperCase() ?? '';
  let requestBody: { [key: string]: string } = {
    patient_mrn: caseRun.patient_identifier.number
  };
  if (!!dateFilter) {
    requestBody = {
      ...requestBody,
      start_date: dateFilter
    };
  }
  if (!!endDateFilter) {
    requestBody = {
      ...requestBody,
      end_date: endDateFilter
    };
  }
  if (status === 'SUCCESS') {
    const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken.tenantPath}/directory/api/v1/entities/patient/entries/functions/get_patient_message_count/execute`;

    try {
      const response = await api.requestJSON<CMxAPI.MessageCounts[]>(queryURL, {
        ...REQUEST_INIT_OPTIONS,
        headers: { Authorization: `Bearer ${xcaliberToken.token}` },
        method: HttpMethods.POST,
        body: JSON.stringify(requestBody)
      });

      return response;
    } catch (error) {
      throw error;
    }
  }
};

const getPatientMessageCountWithoutFilter = async <T>(timelineCountParams) => {
  const { caseRun } = timelineCountParams;
  const xcaliberToken = fetchXcaliberToken();

  const status = xcaliberToken?.status?.toUpperCase() ?? '';
  if (status === 'SUCCESS') {
    const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken.tenantPath}/directory/api/v1/entities/patient/entries/functions/get_patient_message_count/execute`;

    try {
      const response = await api.requestJSON<CMxAPI.MessageCounts[]>(queryURL, {
        ...REQUEST_INIT_OPTIONS,
        headers: { Authorization: `Bearer ${xcaliberToken.token}` },
        method: HttpMethods.POST,
        body: JSON.stringify({
          patient_mrn: caseRun.patient_identifier.number
        })
      });

      return response;
    } catch (error) {
      throw error;
    }
  }
};

export {
  getPatientTimelineWithFilter,
  getPatientTimelineWithoutFilter,
  getPatientMessageCountWithFilter,
  getPatientMessageCountWithoutFilter
};
